import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Card, CardContent, Grid,
  CircularProgress, Alert, FormControl, InputLabel,
  Select, Tooltip
} from '@mui/material';
import {
  Edit, Delete, Add, TableRestaurant,
  Search, Restaurant, OutdoorGrill,
  LocalBar, Chair, Star, CheckCircle,
  RestaurantMenu, EventSeat, Clear,
  Warning, Refresh
} from '@mui/icons-material';
import { tableAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const TablesPage = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openQuickDialog, setOpenQuickDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmplacement, setSelectedEmplacement] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    numero: '',
    capacite: 4,
    emplacement: 'salle',
    description: '',
    statut: 'libre'
  });

  const [quickActionData, setQuickActionData] = useState({
    tableId: null,
    tableNumero: '',
    action: ''
  });

  // Options pour les selects
  const emplacementOptions = [
    { value: 'salle', label: 'Salle principale', icon: <Restaurant /> },
    { value: 'terrasse', label: 'Terrasse', icon: <OutdoorGrill /> },
    { value: 'vip', label: 'Espace VIP', icon: <Star /> },
    { value: 'bar', label: 'Bar', icon: <LocalBar /> },
  ];

  const statutOptions = [
    { value: 'libre', label: 'Libre', color: 'success' },
    { value: 'occupee', label: 'Occupée', color: 'error' },
    { value: 'reservee', label: 'Réservée', color: 'warning' },
  ];

  const capaciteOptions = [2, 4, 6, 8, 10, 12];

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Début du chargement des tables...');
      
      const response = await tableAPI.getAll();
      
      console.log('Réponse complète:', response);
      console.log('Données des tables:', response.data);
      
      // Vérifier le format de la réponse
      let tablesData = [];
      
      if (response.data && Array.isArray(response.data)) {
        // Bon format : tableau direct
        tablesData = response.data;
        console.log(`${tablesData.length} tables chargées`);
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // Format paginé Django REST
        tablesData = response.data.results;
        console.log(`${tablesData.length} tables chargées (format paginé)`);
      } else {
        // Format inattendu
        console.warn('Format de données inattendu:', response.data);
        setError('Format de données inattendu de l\'API');
        tablesData = [];
      }
      
      setTables(tablesData);
      
    } catch (error) {
      console.error('Erreur détaillée:', error);
      console.error('Réponse d\'erreur:', error.response);
      
      let errorMessage = 'Erreur lors du chargement des tables';
      
      if (error.response) {
        if (error.response.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          }
        }
        
        if (error.response.status === 404) {
          errorMessage = 'Endpoint /tables/ non trouvé. Vérifiez votre configuration backend.';
        } else if (error.response.status === 403) {
          errorMessage = 'Accès interdit. Vérifiez vos permissions.';
        } else if (error.response.status === 401) {
          errorMessage = 'Non authentifié. Veuillez vous reconnecter.';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      setTables([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (tablesData) => {
    const total = tablesData.length;
    const libres = tablesData.filter(t => t.statut === 'libre').length;
    const occupees = tablesData.filter(t => t.statut === 'occupee').length;
    const reservees = tablesData.filter(t => t.statut === 'reservee').length;
    const totalPlaces = tablesData.reduce((sum, t) => sum + (t.capacite || 0), 0);
    const tauxOccupation = total > 0 ? Math.round((occupees / total) * 100) : 0;
    
    return {
      total,
      libres,
      occupees,
      reservees,
      tauxOccupation,
      totalPlaces
    };
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const resetForm = () => {
    setFormData({
      numero: '',
      capacite: 4,
      emplacement: 'salle',
      description: '',
      statut: 'libre'
    });
    setIsEditing(false);
  };

  const handleOpenDialog = (table = null) => {
    if (table) {
      setFormData({
        id: table.id,
        numero: table.numero || '',
        capacite: table.capacite || 4,
        emplacement: table.emplacement || 'salle',
        description: table.description || '',
        statut: table.statut || 'libre'
      });
      setIsEditing(true);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleOpenQuickDialog = (table, action) => {
    setQuickActionData({
      tableId: table.id,
      tableNumero: table.numero,
      action: action
    });
    setOpenQuickDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.numero.trim()) {
      toast.error('Le numéro de table est requis');
      return;
    }

    if (formData.capacite < 1) {
      toast.error('La capacité doit être au moins 1');
      return;
    }

    setSubmitting(true);

    try {
      if (isEditing) {
        await tableAPI.update(formData.id, formData);
        toast.success('Table modifiée avec succès');
      } else {
        await tableAPI.create(formData);
        toast.success('Table créée avec succès');
      }
      
      setOpenDialog(false);
      resetForm();
      fetchTables();
    } catch (error) {
      console.error('Erreur:', error);
      let errorMessage = 'Erreur lors de l\'enregistrement';
      
      if (error.response?.data) {
        if (error.response.data.numero && Array.isArray(error.response.data.numero)) {
          errorMessage = `Numéro: ${error.response.data.numero.join(', ')}`;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickAction = async () => {
    try {
      const { tableId, action } = quickActionData;
      
      if (action === 'occuper') {
        await tableAPI.setStatus(tableId, 'occupee');
        toast.success('Table marquée comme occupée');
      } else if (action === 'liberer') {
        await tableAPI.freeTable(tableId);
        toast.success('Table libérée');
      } else if (action === 'reserver') {
        await tableAPI.setStatus(tableId, 'reservee');
        toast.success('Table réservée');
      } else if (action === 'annuler_reservation') {
        await tableAPI.setStatus(tableId, 'libre');
        toast.success('Réservation annulée');
      }
      
      setOpenQuickDialog(false);
      fetchTables();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors de l\'action';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette table ?')) {
      try {
        await tableAPI.delete(id);
        toast.success('Table supprimée avec succès');
        fetchTables();
      } catch (error) {
        console.error('Erreur:', error);
        const errorMessage = error.response?.data?.detail || 
                            error.message ||
                            'Erreur lors de la suppression';
        toast.error(errorMessage);
      }
    }
  };

  const getEmplacementIcon = (emplacement) => {
    const option = emplacementOptions.find(e => e.value === emplacement);
    return option ? option.icon : <TableRestaurant />;
  };

  const getEmplacementLabel = (emplacement) => {
    const option = emplacementOptions.find(e => e.value === emplacement);
    return option ? option.label : emplacement;
  };

  const getStatutColor = (statut) => {
    const option = statutOptions.find(s => s.value === statut);
    return option ? option.color : 'default';
  };

  const getStatutLabel = (statut) => {
    const option = statutOptions.find(s => s.value === statut);
    return option ? option.label : statut;
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case 'libre': return <CheckCircle />;
      case 'occupee': return <RestaurantMenu />;
      case 'reservee': return <EventSeat />;
      default: return <TableRestaurant />;
    }
  };

  const getAvailableActions = (table) => {
    const actions = [];
    
    if (table.statut === 'libre') {
      actions.push({ label: 'Occuper', action: 'occuper', color: 'error', icon: <RestaurantMenu /> });
      actions.push({ label: 'Réserver', action: 'reserver', color: 'warning', icon: <EventSeat /> });
    } else if (table.statut === 'occupee') {
      actions.push({ label: 'Libérer', action: 'liberer', color: 'success', icon: <CheckCircle /> });
    } else if (table.statut === 'reservee') {
      actions.push({ label: 'Annuler réservation', action: 'annuler_reservation', color: 'default', icon: <Clear /> });
      actions.push({ label: 'Occuper', action: 'occuper', color: 'error', icon: <RestaurantMenu /> });
    }
    
    return actions;
  };

  const filteredTables = tables.filter(table => {
    const matchesSearch = table.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         table.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matchesEmplacement = selectedEmplacement === 'all' || table.emplacement === selectedEmplacement;
    const matchesStatus = selectedStatus === 'all' || table.statut === selectedStatus;
    
    return matchesSearch && matchesEmplacement && matchesStatus;
  });

  const stats = calculateStats(tables);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        p={3}
      >
        <CircularProgress />
        <Typography ml={2}>Chargement des tables...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        p: 3,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <TableRestaurant sx={{ mr: 2, verticalAlign: 'middle' }} />
              Gestion des Tables
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Gérez l'occupation et la disposition des tables du restaurant
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher une table..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { borderRadius: 1 }
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                height: 40,
                px: 3,
                borderRadius: 1
              }}
            >
              Nouvelle Table
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTables}
              sx={{ borderRadius: 1 }}
            >
              Actualiser
            </Button>
          </Grid>
        </Grid>

        {/* Cartes statistiques */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Total Tables
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Libres
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.libres}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Occupées
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {stats.occupees}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Réservées
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.reservees}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Taux d'occupation
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.tauxOccupation}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Places totales
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.totalPlaces}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Message d'erreur */}
      {error && (
        <Box mb={3}>
          <Alert 
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button 
                variant="outlined" 
                size="small" 
                onClick={fetchTables}
              >
                Réessayer
              </Button>
            }
          >
            <Typography variant="body1">
              {error}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Filtres */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Emplacement</InputLabel>
            <Select
              value={selectedEmplacement}
              label="Emplacement"
              onChange={(e) => setSelectedEmplacement(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Tous les emplacements</MenuItem>
              {emplacementOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {option.icon}
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={selectedStatus}
              label="Statut"
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              {statutOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Chip 
                    label={option.label} 
                    size="small" 
                    color={option.color}
                    variant="outlined"
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Tableau des tables */}
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Numéro</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Emplacement</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Capacité</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Services</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">Actions Rapides</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTables.length > 0 ? (
                filteredTables.map((table) => {
                  const availableActions = getAvailableActions(table);
                  
                  return (
                    <TableRow key={table.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Typography fontWeight="bold" variant="h6">
                          {table.numero}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getEmplacementIcon(table.emplacement)}
                          <Typography>
                            {getEmplacementLabel(table.emplacement)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chair fontSize="small" />
                          <Typography fontWeight="medium">
                            {table.capacite} places
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          icon={getStatutIcon(table.statut)}
                          label={getStatutLabel(table.statut)}
                          color={getStatutColor(table.statut)}
                          size="small"
                          sx={{ borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>
                          {table.nombre_services || 0} services
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                          {table.description || 'Aucune description'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }} align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          {availableActions.map((action, index) => (
                            <Tooltip key={index} title={action.label}>
                              <IconButton
                                color={action.color}
                                size="small"
                                onClick={() => handleOpenQuickDialog(table, action.action)}
                                sx={{ 
                                  '&:hover': { backgroundColor: `${action.color}.light`, color: 'white' }
                                }}
                              >
                                {action.icon}
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }} align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenDialog(table)}
                            size="small"
                            sx={{ 
                              '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(table.id)}
                            size="small"
                            sx={{ 
                              '&:hover': { backgroundColor: 'error.light', color: 'white' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      {/* <TableRestaurant sx={{ fontSize: 60, color: 'text.disabled' }} /> */}
                      <Typography variant="body1" color="textSecondary">
                        {searchTerm || selectedEmplacement !== 'all' || selectedStatus !== 'all'
                          ? 'Aucune table ne correspond aux critères de recherche'
                          : 'Aucune table trouvée'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Créez votre première table pour commencer
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog d'édition/création */}
      <Dialog 
        open={openDialog} 
        onClose={() => !submitting && setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TableRestaurant />
            <Typography variant="h6">
              {isEditing ? 'Modifier la Table' : 'Nouvelle Table'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Numéro de table"
              fullWidth
              value={formData.numero}
              onChange={(e) => setFormData({...formData, numero: e.target.value})}
              required
              error={!formData.numero.trim()}
              helperText={!formData.numero.trim() ? 'Ce champ est requis' : 'Ex: T1, T2, T10...'}
              disabled={submitting}
              variant="outlined"
              size="small"
            />
            
            <FormControl fullWidth>
              <InputLabel>Emplacement</InputLabel>
              <Select
                value={formData.emplacement}
                label="Emplacement"
                onChange={(e) => setFormData({...formData, emplacement: e.target.value})}
                disabled={submitting}
                sx={{ borderRadius: 1 }}
              >
                {emplacementOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {option.icon}
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Capacité (places)</InputLabel>
              <Select
                value={formData.capacite}
                label="Capacité (places)"
                onChange={(e) => setFormData({...formData, capacite: e.target.value})}
                disabled={submitting}
                sx={{ borderRadius: 1 }}
              >
                {capaciteOptions.map((capacity) => (
                  <MenuItem key={capacity} value={capacity}>
                    {capacity} places
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Description de la table..."
              disabled={submitting}
              variant="outlined"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => !submitting && setOpenDialog(false)} 
            variant="outlined"
            disabled={submitting}
            sx={{ borderRadius: 1 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.numero.trim() || formData.capacite < 1}
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Edit /> : <Add />)}
            sx={{ borderRadius: 1 }}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'action rapide */}
      <Dialog 
        open={openQuickDialog} 
        onClose={() => setOpenQuickDialog(false)} 
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            {quickActionData.action === 'occuper' && 'Occuper la table'}
            {quickActionData.action === 'liberer' && 'Libérer la table'}
            {quickActionData.action === 'reserver' && 'Réserver la table'}
            {quickActionData.action === 'annuler_reservation' && 'Annuler la réservation'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Typography>
              {quickActionData.action === 'occuper' && 
                `Voulez-vous marquer la table ${quickActionData.tableNumero} comme occupée ?`}
              {quickActionData.action === 'liberer' && 
                `Voulez-vous libérer la table ${quickActionData.tableNumero} ? Le nombre de services sera incrémenté.`}
              {quickActionData.action === 'reserver' && 
                `Voulez-vous réserver la table ${quickActionData.tableNumero} ?`}
              {quickActionData.action === 'annuler_reservation' && 
                `Voulez-vous annuler la réservation de la table ${quickActionData.tableNumero} ?`}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => setOpenQuickDialog(false)} 
            variant="outlined"
            sx={{ borderRadius: 1 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleQuickAction}
            variant="contained"
            color={
              quickActionData.action === 'occuper' ? 'error' :
              quickActionData.action === 'liberer' ? 'success' :
              quickActionData.action === 'annuler_reservation' ? 'default' : 'warning'
            }
            sx={{ borderRadius: 1 }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablesPage;